/**
 * 在 TranslationRecordView 内挂载 CR V3 WS 观众数据源
 */

import { onMounted, onUnmounted } from 'vue'
import { message } from 'ant-design-vue'
import { readJoinSession } from './useCrV3JoinSession'
import { useCrV3ViewerWs } from './useCrV3ViewerWs'
import type { CrV3SessionSnapshot, ViewerSubtitleRow } from '../types'
import type { ViewerLanguageItem } from '@/modules/speech-translation/api/viewer'
import { MAX_DISPLAY_LINES } from '@/modules/speech-translation/constants'

export interface TranslationRecordFeedApi {
  applyInit: (data: {
    fragments?: ViewerSubtitleRow[]
    preview?: ViewerSubtitleRow | null
    sourceLanguage?: unknown
    targetLanguages?: unknown
    subtitleStyle?: unknown
  }) => void
  isPreviewPayloadEmpty: (p: ViewerSubtitleRow | null) => boolean
  fragments: { value: ViewerSubtitleRow[] }
  previewLive: { value: ViewerSubtitleRow | null }
  loading: { value: boolean }
  /** 课后 session 快照（V3 overlay） */
  applySessionSnapshot?: (session: CrV3SessionSnapshot) => void
}

/** meetingCode 存在时自动 connect / disconnect */
export function useCrV3ViewerFeed(meetingCode: string, api: TranslationRecordFeedApi) {
  const session = readJoinSession(meetingCode)
  const viewerName = session?.displayName ?? ''
  const languageCode = session?.languageCode ?? ''

  const { connect, disconnect } = useCrV3ViewerWs({
    meetingCode,
    viewerName,
    languageCode,
    callbacks: {
      onInit: (data) => {
        api.applyInit({
          fragments: data.fragments,
          preview: data.preview,
          sourceLanguage: data.sourceLanguage,
          targetLanguages: data.targetLanguages,
          subtitleStyle: data.subtitleStyle,
        })
      },
      onSubtitleStyleUpdate: (style) => {
        api.applyInit({ subtitleStyle: style })
      },
      onPreview: (row) => {
        api.previewLive.value =
          row && !api.isPreviewPayloadEmpty(row) ? row : null
      },
      onFragment: (row) => {
        api.fragments.value = [...api.fragments.value, row].slice(-MAX_DISPLAY_LINES)
        api.previewLive.value = null
      },
      onHistoryCleared: () => {
        api.fragments.value = []
        api.previewLive.value = null
      },
      onSessionLanguagesChanged: (langs) => {
        api.applyInit({
          sourceLanguage: langs.sourceLanguage,
          targetLanguages: langs.targetLanguages,
        })
      },
      onSessionSnapshot: (session) => {
        api.applySessionSnapshot?.(session)
      },
      onError: (msg) => {
        if (msg) {
          message.error(msg)
        }
      },
      onLoading: (v) => {
        api.loading.value = v
      },
    },
  })

  onMounted(() => {
    if (!session) {
      return
    }
    connect()
  })
  onUnmounted(disconnect)

  return { connect, disconnect }
}
